import { createFeatureSelector, createSelector } from '@ngrx/store';
import { State } from './wellness.state';
import { API, Wellness } from '@app/core/models';
import * as Adapters from './wellness.adapters';
import moment from 'moment-timezone';

export const selectFeature = createFeatureSelector<State>('wellnessState');

export const selectMenu = createSelector(selectFeature, (state) => state.menu);

export const selectMenuVM = createSelector(selectFeature, (state) => {
  const dateFormat = 'ddd MMM D, YYYY';
  if (!state.menu) {
    return {
      hotel: 'No Hotel Selected',
      date: moment().format(dateFormat),
    };
  }
  const hotel = state.hotels.find(({ entId }) => entId === state.menu?.entId);
  return {
    hotel: hotel?.entName,
    date: moment(state.menu.date).format(dateFormat),
  } as Wellness.MenuVM;
});

const completionReportSelectors = Adapters.completionReports.getSelectors();

const selectCompletionReportsState = createSelector(
  selectFeature,
  (state) => state.completionReports,
);

const selectCompletionReportEntities = createSelector(
  selectCompletionReportsState,
  completionReportSelectors.selectEntities,
);

export const selectCompletionReportVM = createSelector(
  selectFeature,
  selectCompletionReportEntities,
  (state, reports) => {
    const id = Adapters.getCompletionReportId(state.menu!);
    const report = reports[id];
    return createCompletionReportVM(report);
  },
);

function createCompletionReportVM(
  report?: Wellness.CompletionReportEntity,
): Wellness.CompletionReportVM {
  if (!report) {
    return {
      surveys: [],
      completionRate: '0%',
    };
  }
  return {
    surveys: report.surveys.slice().sort(byShiftStart).map(toSurveyVM),
    completionRate: `${(report.completionRate * 100).toFixed()}%`,
  };
}

function toSurveyVM(survey: Wellness.Survey): Wellness.SurveyVM {
  return {
    employee: {
      name: `${survey.employee.lastName}, ${survey.employee.firstName}`,
      homeDept: survey.employee.homeDept,
      startTime: getStartTime(survey.employee),
      phoneNumber: survey.employee.phoneNumber,
    },
    isComplete: survey.isComplete ? 'Yes' : 'No',
  };
}

function getStartTime(employee: Wellness.Employee): string {
  switch (employee.scheduleType) {
    case 'SCHEDULED':
      return moment(employee.startTime, 'HH:mm:ss').format('h:mm A');
    case 'NOT_SCHEDULED':
      return 'Not Scheduled';
    case 'NO_PUNCH':
      return 'No Punch';
  }
}

function byShiftStart(
  survey1: Wellness.Survey,
  survey2: Wellness.Survey,
): number {
  if (survey1.employee.scheduleType === 'SCHEDULED') {
    return survey2.employee.scheduleType === 'SCHEDULED'
      ? survey1.employee.startTime.localeCompare(survey2.employee.startTime)
      : -1;
  } else {
    return survey2.employee.scheduleType === 'SCHEDULED' ? 1 : 0;
  }
}

const selectApiState = createSelector(selectFeature, (state) => state.api);

export const selectHotels = createSelector(
  selectFeature,
  (state) => state.hotels,
);

export const selectIsHotelListLoaded = createSelector(
  selectApiState,
  selectHotels,
  (state, hotels) => {
    const isHotelListCached = hotels.length > 0;
    const isDoneFetching =
      state.getTeamWellnessChecksHotels === API.LoadingState.LOADED;
    return isHotelListCached || isDoneFetching;
  },
);

export const selectIsCompletionReportLoaded = createSelector(
  selectApiState,
  selectMenu,
  selectCompletionReportEntities,
  (state, menu, reports) => {
    const reportId = menu ? Adapters.getCompletionReportId(menu) : '';
    const isReportDataCached = !!reports[reportId];
    const isDoneFetching =
      state.getTeamWellnessCheckInReport === API.LoadingState.LOADED;
    return isReportDataCached || isDoneFetching;
  },
);

export const selectIsHotelSelected = createSelector(
  selectMenu,
  (state) => !!(state && state.entId),
);

const selectWellnessRisks = createSelector(
  selectFeature,
  (state) => state.risks,
);

export const selectWellnessRisksVM = createSelector(
  selectMenu,
  selectWellnessRisks,
  (menu, risks) => {
    if (!menu) {
      return [];
    }
    return risks
      .filter((risk) => risk.entId === menu.entId)
      .map(createWellnessRiskVM);
  },
);

function createWellnessRiskVM(risk: Wellness.Risk): Wellness.RiskVM {
  return {
    employee: {
      name: `${risk.employee.lastName}, ${risk.employee.firstName}`,
      homeDept: risk.employee.homeDept,
      phoneNumber: risk.employee.phoneNumber,
    },
    dateReportedET: moment(risk.dateReportedUTC)
      .tz('America/New_York')
      .format('M/D/YYYY H:mm:ss'),
  };
}

export const selectIsWellnessRisksLoaded = createSelector(
  selectApiState,
  selectWellnessRisks,
  (state, risks) => {
    const isRisksCached = risks.length > 0;
    const isDoneFetching =
      state.getTeamWellnessCheckInRisks === API.LoadingState.LOADED;
    return isRisksCached || isDoneFetching;
  },
);
